/**
 * Het wijzigingslog: wie, wat en wanneer — en op een klik wat er precies is gebeurd.
 *
 * De regels komen zoals de API ze vastlegt: een actiesleutel (`stap-ingesteld`) en een
 * detailtekst die per actie een andere vorm heeft (`fotoVerplicht=true`,
 * `1. 0719-25-6504-10 · 2. …`, een JSON-object). Bruikbaar voor een audit, maar niet om
 * te lezen. Daarom twee lagen:
 *
 *  - in de tabel staat de actie in gewoon Nederlands en de details verkort;
 *  - een klik op de regel klapt hem uit, met de velden los benoemd: veldnaam,
 *    oude/nieuwe waarde in woorden ("ja" in plaats van `true`), het volledige tijdstip
 *    en de technische sleutel voor wie hem toch nodig heeft.
 *
 * De omzetting staat hier en niet in de API: het logboek is bewust een platte
 * vastlegging die niet meebeweegt met de schermen. Wat er niet in de tabellen staat,
 * valt terug op de ruwe tekst — een nieuwe actie in de API levert dus nooit een leeg
 * veld op, alleen een iets minder mooie regel.
 */
import { useState } from 'react';

import type { AuditRegel } from '../api';
import { datumTijd } from '../gegevens';
import { IconChevronOnder } from './Iconen';

/** De acties die de API vastlegt, in gewoon Nederlands. */
const ACTIELABEL: Record<string, string> = {
  'stap-afgestreept': 'Stap afgestreept',
  'stap-ingesteld': 'Stap ingesteld',
  'volgorde-gewijzigd': 'Volgorde in de route gewijzigd',
  'route-hersteld': 'Route hersteld',
  'routes-bijgewerkt': 'Routes bijgewerkt',
  'rework-gemeld': 'Rework gemeld',
  'rework-teruggegeven': 'Rework terug bij de eigen kar',
  'foto-toegevoegd': 'Foto toegevoegd',
  'onderdeel-aangepast': 'Onderdeel aangepast',
  'bouwelement-gesplitst': 'Bouwelement gesplitst',
  'vrijgegeven-voor-productie': 'Vrijgegeven voor productie',
  'on-hold-gezet': 'On hold gezet',
  'on-hold-opgeheven': 'On hold opgeheven',
  'afwijking-goedgekeurd': 'Afwijkende waarde goedgekeurd',
  'goedkeuring-ingetrokken': 'Goedkeuring ingetrokken',
  'houtstaat-geimporteerd': 'Houtstaat geïmporteerd',
  'coderingen-gewijzigd': 'Coderingen gewijzigd',
  'algemene-lijsten-gewijzigd': 'Algemene lijsten gewijzigd',
  'bouwnummer-bijgewerkt': 'Bouwnummer bijgewerkt',
  'planning-gewijzigd': 'Planning gewijzigd',
  'planning-vastgezet': 'Planning vastgezet',
  'bewerkingsregel-opgeslagen': 'Bewerkingsregel opgeslagen',
  'bewerkingsregel-verwijderd': 'Bewerkingsregel verwijderd',
  'stl-geladen': '3D-model geladen',
  'model-geladen': '3D-model geladen',
  gearchiveerd: 'Export gearchiveerd',
};

/** Waar de detailtekst over gaat, per actie: kop boven het uitklapblok. */
const DETAILKOP: Record<string, string> = {
  'stap-ingesteld': 'Gewijzigde instellingen',
  'onderdeel-aangepast': 'Gewijzigde velden',
  'stap-afgestreept': 'Afgestreepte stap',
  'volgorde-gewijzigd': 'Verplaatste stap',
  'rework-gemeld': 'Stap en reden',
  'rework-teruggegeven': 'Wat er terugging',
  'on-hold-gezet': 'Reden',
  'foto-toegevoegd': 'Bestand',
  'stl-geladen': 'Bestand',
  'model-geladen': 'Bestand',
  'planning-gewijzigd': 'Nieuwe volgorde',
  'planning-vastgezet': 'Vastgezette volgorde',
  'bouwelement-gesplitst': 'Nieuwe karren',
  'houtstaat-geimporteerd': 'Omvang van de import',
  'afwijking-goedgekeurd': 'Bevinding',
  'goedkeuring-ingetrokken': 'Bevinding',
  'bewerkingsregel-opgeslagen': 'Code van de regel',
  'bewerkingsregel-verwijderd': 'Code van de regel',
  gearchiveerd: 'Locatie',
};

/** Wat het onderwerp van de regel is, per actie. */
const ONDERWERPKOP: Record<string, string> = {
  'stap-ingesteld': 'Kar en stap',
  'stap-afgestreept': 'Kar',
  'volgorde-gewijzigd': 'Kar',
  'route-hersteld': 'Kar',
  'rework-gemeld': 'Kar',
  'rework-teruggegeven': 'Van reworkkar naar kar',
  'foto-toegevoegd': 'Kar en stap',
  'onderdeel-aangepast': 'Onderdeelnummer',
  'on-hold-gezet': 'Bouwelement',
  'on-hold-opgeheven': 'Bouwelement',
  'bouwelement-gesplitst': 'Bouwelement',
  'vrijgegeven-voor-productie': 'Bouwelement',
  'houtstaat-geimporteerd': 'Bron',
  'stl-geladen': 'Bouwelement of onderdeel',
  'model-geladen': 'Bouwelement of onderdeel',
  'bewerkingsregel-opgeslagen': 'Regel',
  'bewerkingsregel-verwijderd': 'Regel',
};

/**
 * De veldnamen zoals ze in de details staan, met hun naam op het scherm.
 *
 * Dit zijn dezelfde namen als in de materiaalstaat en het stap-menu, zodat wie een
 * regel leest weet welk vakje er is aangepast.
 */
const VELDLABEL: Record<string, string> = {
  fotoVerplicht: 'Foto verplicht',
  normZichtbaar: 'Normtijd zichtbaar voor productie',
  normtijd: 'Normtijd',
  opmerking: 'Opmerking',
  aantal: 'Aantal',
  benaming: 'Benaming',
  lengte: 'Lengte',
  breedte: 'Breedte',
  dikte: 'Dikte',
  materiaal: 'Materiaal',
  dekkend: 'Dekkend',
  overmaatLengte: 'Overmaat lengte',
  overmaatBreedte: 'Overmaat breedte',
  overmaatKarweiLengte: 'Overmaat karwei lengte',
  overmaatKarweiBreedte: 'Overmaat karwei breedte',
  zijde0: 'Zijde 0 (zichtvlak)',
  zijde5: 'Zijde 5 (achterzijde)',
  zijde1: 'Zijde 1 (kant)',
  zijde2: 'Zijde 2 (kant)',
  zijde3: 'Zijde 3 (kant)',
  zijde4: 'Zijde 4 (kant)',
  naam: 'Naam',
  kleur: 'Kleur',
  versie: 'Versie',
  revisie: 'Revisie',
  omschrijving: 'Omschrijving',
  fotoVerplichtBij: 'Foto verplicht bij',
};

export const actieLabel = (actie: string): string =>
  ACTIELABEL[actie] ?? actie.replace(/-/g, ' ').replace(/^./, (c) => c.toUpperCase());

/** `true` → `ja`, een leeggemaakt veld → wat het is. */
function waardeLabel(veld: string, waarde: string): string {
  const schoon = waarde.trim();
  if (schoon === '') return 'leeggemaakt';
  if (schoon === 'true') return 'ja';
  if (schoon === 'false') return 'nee';
  if (schoon === 'undefined' || schoon === 'null') return 'leeggemaakt';
  // `normtijd=12+1.5/stuk` is de enige samengestelde waarde in het logboek.
  if (veld === 'normtijd') {
    const treffer = /^([\d.,]+)\+([\d.,]+)\/stuk$/.exec(schoon);
    if (treffer) {
      const komma = (v: string) => v.replace('.', ',');
      return `${komma(treffer[1] ?? '')} min opstart + ${komma(treffer[2] ?? '')} min per stuk`;
    }
  }
  return schoon;
}

interface Detail {
  kop: string;
  waarde: string;
}

/**
 * De detailtekst uitgesplitst in benoemde velden.
 *
 * Drie vormen komen voorbij: JSON (het bijwerken van een bouwnummer), `veld=waarde`
 * gescheiden door `·`, en vrije tekst (een reden, een bestandsnaam, een opsomming).
 * Alleen de eerste twee zijn uit te splitsen; vrije tekst blijft één blok, want het
 * verzinnen van velden die er niet zijn maakt het log onbetrouwbaar.
 */
function splitsDetails(actie: string, details: string): Detail[] {
  const tekst = details.trim();
  if (!tekst) return [];

  if (tekst.startsWith('{')) {
    try {
      const object = JSON.parse(tekst) as Record<string, unknown>;
      return Object.entries(object).map(([veld, waarde]) => ({
        kop: VELDLABEL[veld] ?? veld,
        waarde: waardeLabel(veld, Array.isArray(waarde) ? waarde.join(', ') : String(waarde)),
      }));
    } catch {
      // Geen geldige JSON: dan als vrije tekst hieronder.
    }
  }

  // Alleen deze twee acties leggen hun details als `veld=waarde` vast. Bij de rest kan
  // een reden of een bestandsnaam toevallig ook een `=` bevatten, en dan zou de tekst
  // ten onrechte als veldnaam worden gelezen.
  const heeftVelden = actie === 'stap-ingesteld' || actie === 'onderdeel-aangepast';
  const delen = tekst.split(' · ');
  const paren =
    heeftVelden &&
    delen.every((deel) => /^[A-Za-z][A-Za-z0-9]*=/.test(deel) || deel === 'opmerking');
  if (paren) {
    return delen.map((deel) => {
      const scheiding = deel.indexOf('=');
      if (scheiding === -1) {
        // `opmerking` wordt zonder waarde vastgelegd: de tekst zelf hoort niet in een
        // logboek dat door de hele afdeling gelezen wordt.
        return { kop: VELDLABEL[deel] ?? deel, waarde: 'aangepast' };
      }
      const veld = deel.slice(0, scheiding);
      return { kop: VELDLABEL[veld] ?? veld, waarde: waardeLabel(veld, deel.slice(scheiding + 1)) };
    });
  }

  return [{ kop: DETAILKOP[actie] ?? 'Details', waarde: tekst }];
}

/** De details verkort, voor de dichtgeklapte regel. */
function korteDetails(actie: string, details: string | undefined): string {
  if (!details) return '';
  const velden = splitsDetails(actie, details);
  if (velden.length === 0) return '';
  if (velden.length === 1 && velden[0]) {
    const enkel = velden[0];
    return enkel.kop === (DETAILKOP[actie] ?? 'Details')
      ? enkel.waarde
      : `${enkel.kop}: ${enkel.waarde}`;
  }
  return velden.map((v) => `${v.kop}: ${v.waarde}`).join(' · ');
}

/** Het volledige tijdstip, voor het uitklapblok. */
const volledigTijdstip = (iso: string): string =>
  new Date(iso).toLocaleString('nl-NL', {
    weekday: 'long',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });

interface Props {
  regels: AuditRegel[];
  /** Wat er staat als er nog niets is vastgelegd. */
  leegtekst?: string;
}

export function Wijzigingslog({ regels, leegtekst }: Props) {
  const [open, setOpen] = useState<string | null>(null);

  if (regels.length === 0) {
    return <div className="hs-leeg">{leegtekst ?? 'Er zijn nog geen wijzigingen vastgelegd.'}</div>;
  }

  return (
    <table className="hs-tabel hs-logtabel">
      <thead>
        <tr>
          <th style={{ width: 26 }} aria-label="Uitklappen" />
          <th>Tijdstip</th>
          <th>Gebruiker</th>
          <th>Rol</th>
          <th>Actie</th>
          <th>Onderwerp</th>
          <th>Details</th>
        </tr>
      </thead>
      {regels.map((regel) => {
        const uitgeklapt = open === regel.id;
        const velden = splitsDetails(regel.actie, regel.details ?? '');
        return (
          <tbody key={regel.id}>
            <tr
              className={`hs-logrij${uitgeklapt ? ' hs-logrij--open' : ''}`}
              onClick={() => setOpen(uitgeklapt ? null : regel.id)}
              tabIndex={0}
              role="button"
              aria-expanded={uitgeklapt}
              title="Klik voor de volledige wijziging"
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setOpen(uitgeklapt ? null : regel.id);
                }
              }}
            >
              <td className="hs-logrij__pijl">
                <IconChevronOnder maat={12} />
              </td>
              <td className="hs-mono">{datumTijd(regel.tijdstip)}</td>
              <td>{regel.gebruiker}</td>
              <td className="hs-muted">{regel.rol}</td>
              <td>{actieLabel(regel.actie)}</td>
              <td>{regel.onderwerp}</td>
              <td className="hs-muted" style={{ whiteSpace: 'normal' }}>
                {korteDetails(regel.actie, regel.details)}
              </td>
            </tr>
            {uitgeklapt ? (
              <tr className="hs-loguit">
                <td colSpan={7}>
                  <div className="hs-loguit__blok">
                    <dl className="hs-loguit__lijst">
                      <div>
                        <dt>Wat</dt>
                        <dd>{actieLabel(regel.actie)}</dd>
                      </div>
                      <div>
                        <dt>Wanneer</dt>
                        <dd>{volledigTijdstip(regel.tijdstip)}</dd>
                      </div>
                      <div>
                        <dt>Door</dt>
                        <dd>
                          {regel.gebruiker} · {regel.rol}
                        </dd>
                      </div>
                      <div>
                        <dt>{ONDERWERPKOP[regel.actie] ?? 'Onderwerp'}</dt>
                        <dd>{regel.onderwerp || '—'}</dd>
                      </div>
                    </dl>

                    {velden.length > 0 ? (
                      <div className="hs-loguit__velden">
                        <span className="hs-loguit__kop">
                          {DETAILKOP[regel.actie] ?? 'Details'}
                        </span>
                        <dl className="hs-loguit__lijst">
                          {velden.map((veld, i) => (
                            <div key={`${veld.kop}-${i}`}>
                              <dt>{veld.kop}</dt>
                              <dd>{veld.waarde}</dd>
                            </div>
                          ))}
                        </dl>
                      </div>
                    ) : (
                      <p className="hs-muted">Bij deze wijziging zijn geen details vastgelegd.</p>
                    )}

                    <p className="hs-mono hs-muted-licht hs-loguit__sleutel">
                      {regel.actie} · {regel.id}
                    </p>
                  </div>
                </td>
              </tr>
            ) : null}
          </tbody>
        );
      })}
    </table>
  );
}
